import { toastState } from "@/atoms/system/toast-state";
import { signUpPersonalPartnerPayloadState } from "@/atoms/user/sign-up-personal-partner-payload-state";
import { ButtonColor } from "@/common/enum/button-color";
import { CheckboxColor } from "@/common/enum/checkbox-color";
import { LoginPath } from "@/common/enum/path";
import { TextInputColor } from "@/common/enum/text-input-color";
import { formatBusinessNumber } from "@/common/util/format-business-number";
import { formatPhoneNumber } from "@/common/util/format-phone-number";
import { validateEmail } from "@/common/validator/validate-email";
import { AddressSearchButton } from "@/components/button/AddressSearchButton";
import { CustomHelperText } from "@/components/CustomHelperText";
import { TextInputLabel } from "@/components/label/TextInputLabel";
import { UseRuleModal } from "@/components/modal/UseRuleModal";
import { Body14Medium } from "@/components/typography/Body14Medium";
import { Body14Regular } from "@/components/typography/Body14Regular";
import { Body14Underline } from "@/components/typography/Body14Underline";
import { H4 } from "@/components/typography/H4";
import { userService } from "@/services/user-service";
import { SignUpPersonalPartnerPayload } from "@/types/payload/sign-up-personal-partner-payload";
import { Button, Checkbox, FileInput, TextInput } from "flowbite-react";
import { useState } from "react";
import { Address } from "react-daum-postcode";
import { useNavigate } from "react-router-dom";
import { useRecoilState, useSetRecoilState } from "recoil";

// 개인
export default function SignUpBusinessPage() {
  const navigate = useNavigate();
  const [signUpPersonalPartnerPayload, setSignUpPersonalPartnerPayload] =
    useRecoilState(signUpPersonalPartnerPayloadState);
  const setToast = useSetRecoilState(toastState);
  const [aggregateErrors, setAggregateErrors] = useState({
    name: "",
    businessName: "",
    businessNumber: "",
    phone: "",
    road: "",
    jibun: "",
    postalCode: "",
    file: "",
    taxReceiptEmail: "",
  });

  // 전체동의, 발주몰 이용약관 동의, 프로모션 수신동의
  const [isAllAgree, setIsAllAgree] = useState(false);
  const [isUseRuleAgree, setIsUseRuleAgree] = useState(false);
  const [isAdAgree, setIsAdAgree] = useState(false);

  const handleAllAgreeChange = () => {
    setIsAllAgree(!isAllAgree);
    setIsUseRuleAgree(!isUseRuleAgree);
    setIsAdAgree(!isAdAgree);
  };

  const handleUseRuleAgreeChange = () => {
    setIsUseRuleAgree(!isUseRuleAgree);
  };

  const handleAdAgreeChange = () => {
    setIsAdAgree(!isAdAgree);
  };

  const isSignUpPersonalPartnerButtonActive =
    signUpPersonalPartnerPayload.name &&
    signUpPersonalPartnerPayload.businessName &&
    signUpPersonalPartnerPayload.businessNumber &&
    signUpPersonalPartnerPayload.phone &&
    (signUpPersonalPartnerPayload.road || signUpPersonalPartnerPayload.jibun) &&
    signUpPersonalPartnerPayload.postalCode &&
    signUpPersonalPartnerPayload.file &&
    signUpPersonalPartnerPayload.taxReceiptEmail &&
    isUseRuleAgree;

  const handleSignUpPersonalPartnerPayloadChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = event.target;
    setSignUpPersonalPartnerPayload({
      ...signUpPersonalPartnerPayload,
      [name]: value,
    });
  };

  const handleBusinessNumberChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const formattedValue = formatBusinessNumber(event.target.value);

    setSignUpPersonalPartnerPayload({
      ...signUpPersonalPartnerPayload,
      businessNumber: formattedValue,
    });
  };

  const handlePhoneNumberChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const formattedValue = formatPhoneNumber(event.target.value);

    setSignUpPersonalPartnerPayload({
      ...signUpPersonalPartnerPayload,
      phone: formattedValue,
    });
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        alert("이미지 파일만 업로드 가능합니다.");
        return;
      }
      setAggregateErrors({ ...aggregateErrors, file: "" });
      setSignUpPersonalPartnerPayload({
        ...signUpPersonalPartnerPayload,
        file,
      });
    }
  };

  const handleSignUpPersonalPartnerButtonClick = async () => {
    try {
      if (!signUpPersonalPartnerPayload.file) {
        setAggregateErrors({
          ...aggregateErrors,
          file: "사업자등록증을 업로드해주세요.",
        });
        return;
      }

      if (!validateEmail(signUpPersonalPartnerPayload.taxReceiptEmail)) {
        setAggregateErrors({
          ...aggregateErrors,
          taxReceiptEmail: "올바르지 않은 이메일 형식입니다",
        });
        return;
      }

      const payload: SignUpPersonalPartnerPayload = {
        ...signUpPersonalPartnerPayload,
        file: signUpPersonalPartnerPayload.file,
      };

      const response = await userService.signUpPersonalPartner(payload);
      if (response.data.aggregateErrors) {
        setAggregateErrors({
          ...aggregateErrors,
          name: response.data.aggregateErrors.name,
          businessName: response.data.aggregateErrors.businessName,
          businessNumber: response.data.aggregateErrors.businessNumber,
          phone: response.data.aggregateErrors.phone,
          road: response.data.aggregateErrors.road,
          jibun: response.data.aggregateErrors.jibun,
          postalCode: response.data.aggregateErrors.postalCode,
          taxReceiptEmail: response.data.aggregateErrors.taxReceiptEmail,
        });
      }

      if (response.ok) {
        setToast({
          show: true,
          messages: [`회원가입이 완료되었습니다.`, `다시 로그인 해주세요.`],
        });
        navigate(LoginPath.LOGIN);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const setAddress = (address: Address) => {
    setSignUpPersonalPartnerPayload({
      ...signUpPersonalPartnerPayload,
      postalCode: address.zonecode,
      road: address.roadAddress,
      jibun: address.jibunAddress,
    });
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center py-[112px]">
      <div className="flex flex-col items-center w-[400px]">
        <H4 className="text-semantic-text-heading mb-[32px]">
          마스터 발주몰 회원가입
        </H4>
        <div className="flex flex-col w-full overflow-y-auto max-h-[70vh] pb-[60px]">
          <TextInputLabel required>파트너명</TextInputLabel>
          <TextInput
            required
            placeholder="사업자 본인 이름을 입력해주세요"
            name="name"
            value={signUpPersonalPartnerPayload.name}
            onChange={handleSignUpPersonalPartnerPayloadChange}
            color={
              !!aggregateErrors.name
                ? TextInputColor.FAILURE
                : TextInputColor.GRAY
            }
          />
          {!!aggregateErrors.name && (
            <CustomHelperText
              isError={!!aggregateErrors.name}
              text={aggregateErrors.name}
            />
          )}
          <div className="pt-[16px]">
            <TextInputLabel required>상호명</TextInputLabel>
          </div>
          <TextInput
            required
            placeholder="상호명을 입력해주세요"
            name="businessName"
            value={signUpPersonalPartnerPayload.businessName}
            onChange={handleSignUpPersonalPartnerPayloadChange}
            color={
              !!aggregateErrors.businessName
                ? TextInputColor.FAILURE
                : TextInputColor.GRAY
            }
          />
          {!!aggregateErrors.businessName && (
            <CustomHelperText
              isError={!!aggregateErrors.businessName}
              text={aggregateErrors.businessName}
            />
          )}
          <div className="pt-[16px]">
            <TextInputLabel required>사업자번호</TextInputLabel>
          </div>
          <TextInput
            required
            placeholder="000-00-00000"
            name="businessNumber"
            value={signUpPersonalPartnerPayload.businessNumber}
            onChange={handleBusinessNumberChange}
            color={
              !!aggregateErrors.businessNumber
                ? TextInputColor.FAILURE
                : TextInputColor.GRAY
            }
          />
          {!!aggregateErrors.businessNumber && (
            <CustomHelperText
              isError={!!aggregateErrors.businessNumber}
              text={aggregateErrors.businessNumber}
            />
          )}
          <div className="pt-[16px]">
            <TextInputLabel required>사업자등록증</TextInputLabel>
          </div>
          <FileInput required onChange={handleFileChange} />
          <CustomHelperText
            isError={!!aggregateErrors.file}
            text={aggregateErrors.file || "인증 용도로 사용됩니다."}
          />
          <div className="pt-[16px]">
            <TextInputLabel required>세금계산서 발급 이메일</TextInputLabel>
          </div>
          <TextInput
            required
            placeholder="aqara@gmail.com"
            name="taxReceiptEmail"
            value={signUpPersonalPartnerPayload.taxReceiptEmail}
            onChange={handleSignUpPersonalPartnerPayloadChange}
            color={
              !!aggregateErrors.taxReceiptEmail
                ? TextInputColor.FAILURE
                : TextInputColor.GRAY
            }
          />
          {!!aggregateErrors.taxReceiptEmail && (
            <CustomHelperText
              isError={!!aggregateErrors.taxReceiptEmail}
              text={aggregateErrors.taxReceiptEmail}
            />
          )}
          <div className="pt-[16px]">
            <TextInputLabel required>연락처</TextInputLabel>
          </div>
          <TextInput
            required
            placeholder="010-1234-1234"
            name="phone"
            value={signUpPersonalPartnerPayload.phone}
            onChange={handlePhoneNumberChange}
            color={
              !!aggregateErrors.phone
                ? TextInputColor.FAILURE
                : TextInputColor.GRAY
            }
          />
          {!!aggregateErrors.phone && (
            <CustomHelperText
              isError={!!aggregateErrors.phone}
              text={aggregateErrors.phone}
            />
          )}
          <div className="pt-[16px]">
            <TextInputLabel required>주소</TextInputLabel>
          </div>
          <div className="flex">
            <TextInput
              required
              className="mr-[10px] w-[325px]"
              placeholder="우편번호"
              name="postalCode"
              value={signUpPersonalPartnerPayload.postalCode}
              onChange={handleSignUpPersonalPartnerPayloadChange}
              color={
                !!aggregateErrors.postalCode
                  ? TextInputColor.FAILURE
                  : TextInputColor.GRAY
              }
              disabled
            />
            <AddressSearchButton setAddress={setAddress} />
          </div>
          {!!aggregateErrors.postalCode && (
            <CustomHelperText
              isError={!!aggregateErrors.postalCode}
              text={aggregateErrors.postalCode}
            />
          )}
          <TextInput
            required
            className="mt-[8px]"
            placeholder="기본주소"
            name="road"
            value={signUpPersonalPartnerPayload.road}
            onChange={handleSignUpPersonalPartnerPayloadChange}
            color={
              !!aggregateErrors.road
                ? TextInputColor.FAILURE
                : TextInputColor.GRAY
            }
            disabled
          />
          {!!aggregateErrors.road && (
            <CustomHelperText
              isError={!!aggregateErrors.road}
              text={aggregateErrors.road}
            />
          )}
          <TextInput
            className="mt-[8px]"
            placeholder="나머지 주소 (선택 입력 가능)"
            name="detail"
            value={signUpPersonalPartnerPayload.detail}
            onChange={handleSignUpPersonalPartnerPayloadChange}
          />
          <div className="mt-[8px]">
            <TextInputLabel>가입코드</TextInputLabel>
            <TextInput
              placeholder="가입코드를 입력해주세요."
              name="signupCode"
              value={signUpPersonalPartnerPayload.signupCode}
              onChange={handleSignUpPersonalPartnerPayloadChange}
            />
          </div>

          <div className="mt-[8px]">
            <TextInputLabel required>이용약관 동의</TextInputLabel>
          </div>

          <div className="flex flex-col">
            <div className="flex flex-row items-center gap-4">
              <Checkbox
                checked={isAllAgree}
                color={isAllAgree ? CheckboxColor.DARK : ""}
                onChange={handleAllAgreeChange}
              />
              <Body14Medium>전체 동의하기</Body14Medium>
            </div>
            <Body14Regular className="text-semantic-text-tertiary ml-6 mt-2">
              전체 동의에는 필수 및 선택 정보에 대한 동의가 포함되어 있습니다.
              <br />
              개별적으로 동의를 선택 하실 수 있으며, 선택 항목에 대한 동의를
              <br />
              거부하시는 경우에도 서비스 이용이 가능합니다.
            </Body14Regular>
            <hr className="mt-4" />
            <div className="flex flex-row gap-4 mt-4 items-center">
              <Checkbox
                checked={isUseRuleAgree}
                color={isUseRuleAgree ? CheckboxColor.DARK : ""}
                onChange={handleUseRuleAgreeChange}
              />
              <div>
                <a
                  href="https://aqaralife.shop/member/agreement.html"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Body14Underline>발주몰 이용약관 동의</Body14Underline>
                </a>
              </div>
              <Body14Medium className="text-blue-400 ml-auto">
                필수
              </Body14Medium>
            </div>
            <div className="flex flex-row gap-4 mt-4 items-center">
              <Checkbox
                checked={isAdAgree}
                color={isAdAgree ? CheckboxColor.DARK : ""}
                onChange={handleAdAgreeChange}
              />
              <a
                href="https://aqaralife.gitbook.io/archive/agreement/bizletter"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Body14Underline>사업자 뉴스레터 수신동의</Body14Underline>
              </a>
              <Body14Medium className="text-gray-400 ml-auto">
                선택
              </Body14Medium>
            </div>
            <Body14Regular className="text-semantic-text-tertiary ml-6 mt-2">
              수신 동의시 신제품 출시 정보, 프로모션 정보,
              <br />
              아카라라이프의 소식등을 가장 먼저 받으실 수 있습니다.
            </Body14Regular>
          </div>
        </div>

        <div className="fixed bottom-0 left-0 right-0 flex items-center justify-center pt-[32px] pb-[112px] bg-primary-white">
          <Button
            className="w-[400px]"
            color={
              isSignUpPersonalPartnerButtonActive ? ButtonColor.PRIMARY : ""
            }
            disabled={!isSignUpPersonalPartnerButtonActive}
            onClick={handleSignUpPersonalPartnerButtonClick}
          >
            회원가입
          </Button>
        </div>
      </div>
    </div>
  );
}
